/**
 * CANONICAL MODULE — app-platform/shared/syncLock.ts
 *
 * Copied byte-for-byte into every app (`src/lib/syncLock.ts`). No imports. Built for work order W11
 * (task 2.b) from [design/W11-sync-orchestration.md](../design/W11-sync-orchestration.md) §3.1.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * One cross-TAB mutex for a sync drain or reconcile (B10), and a tiny tab bus that tells the other
 * tabs something changed. It is NOT a cross-device lock: across devices the content tag is the only
 * lock (W10), and a lock file on OneDrive would add a stale-lock failure mode for no gain.
 *
 * ── Layers, best first ────────────────────────────────────────────────────────────────────────────
 * 1. **Web Locks** (`navigator.locks.request(name, { ifAvailable })`), as quil does today. The browser
 *    releases it when a tab dies.
 * 2. **A localStorage lease** `{ owner, expiresAt }`, only where `navigator.locks` is missing. Taken
 *    by write, settle 10-40 ms, read back. Renewed every `leaseMs / 3` while held, released on finish
 *    and on `pagehide`; a lease past `expiresAt` is stolen, so a crashed tab costs one lease period.
 *    A BroadcastChannel `release` message wakes `'wait'` callers early; they also poll every 250 ms.
 * 3. **An in-process promise chain**, always underneath (2) and alone when there is neither: still
 *    exclusive inside the tab. This is what quil's "run unlocked" fallback becomes.
 *
 * ── Rules ─────────────────────────────────────────────────────────────────────────────────────────
 * - `'skip'` (background triggers) resolves `{ ran: false }` at once when another holder has it: the
 *   holder's sync covers the shared OneDrive. `'wait'` (cold start, a resolution) queues.
 * - **`maxHoldMs` bounds the holder.** At `maxHoldMs` the `fn`'s signal aborts with a
 *   `SyncLockTimeoutError` (named `AbortError`, so the W10 transport throws it unchanged), `run`
 *   rejects with it, and the lock is RELEASED even if `fn` ignores the signal. A hung fetch can never
 *   starve the other tabs; whatever the abandoned `fn` still sends is guarded by `If-Match`.
 * - **Concurrent callers in one tab queue** (2026-10-08). A `'wait'` while this tab holds the lock,
 *   from code that did not start inside the held `fn` (Settings saving while a drain runs), waits its
 *   turn in FIFO order, exactly like a caller in another tab. No app needs its own in-tab queue.
 * - **The lock covers a drain, never a user prompt.** Open a chooser with `afterRelease(...)`, which
 *   runs once this tab holds nothing, and resolve it under a fresh `run(..., 'wait')`. Calling `run`
 *   in `'wait'` mode from the held `fn`'s synchronous part (directly or through helpers) would wait
 *   on itself, so it throws instead. A browser has no async context, so a re-entrant call made AFTER
 *   the held `fn`'s first `await` is indistinguishable from a concurrent one and queues; if the
 *   holder then awaits it, `maxHoldMs` breaks the deadlock (the holder rejects, the queued call
 *   runs). Bounded, never permanent, and still a bug in the caller: use `afterRelease`.
 */

export type LockMode = 'wait' | 'skip'

export type LockRunResult<T> = { ran: true; value: T } | { ran: false }

export interface SyncLock {
  /** Run `fn` holding the lock. `'skip'` resolves `{ ran: false }` at once if another holder has it. */
  run<T>(fn: (signal: AbortSignal) => Promise<T>, mode: LockMode): Promise<LockRunResult<T>>
  /** True while this tab (this lock object) is running a `fn` under the lock. */
  heldHere(): boolean
  /** Run `cb` once this tab holds nothing: how a chooser is opened from inside a drain. */
  afterRelease(cb: () => void): void
  /** Which layer is in use, for the log line and the tests. */
  readonly layer: 'web-locks' | 'lease' | 'in-tab'
}

/** The slice of `navigator.locks` the lock uses; `navigator.locks` satisfies it. */
export interface LockManagerLike {
  request<T>(name: string, options: { ifAvailable?: boolean }, callback: (lock: unknown) => Promise<T>): Promise<T>
}

/** The slice of `localStorage` the lease uses; `localStorage` satisfies it. */
export interface LeaseStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/** The slice of `BroadcastChannel` used; a `BroadcastChannel` satisfies it. */
export interface BroadcastChannelLike {
  postMessage(message: unknown): void
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void
}

export interface SyncLockConfig {
  /** `${APP_ID}:${accountId}:sync`, so an account switch never shares a lock. */
  name: string
  /** The holder's signal aborts, and the lock is released, past this. Default 120 000. */
  maxHoldMs?: number
  /** `undefined` = `globalThis.navigator.locks` when present; `null` = do not use Web Locks. */
  locks?: LockManagerLike | null
  /** `undefined` = `globalThis.localStorage` when present; `null` = no lease. */
  lease?: LeaseStore | null
  /** `undefined` = a `BroadcastChannel(name)` when present; `null` = poll only. */
  channel?: BroadcastChannelLike | null
  /** Lease period for the fallback. Default 10 000; renewed every third of it while held. */
  leaseMs?: number
  now?: () => number
  random?: () => number
  tabId?: string
}

/** Thrown into the holder's signal, and by `run`, when `fn` held the lock past `maxHoldMs`. */
class SyncLockTimeoutError extends Error {
  readonly syncLockTimeout = true
  readonly heldMs: number
  constructor(name: string, heldMs: number) {
    super(`The sync lock "${name}" was held for ${heldMs} ms and has been released.`)
    // An abort, by name: the W10 transport throws an AbortError unchanged and never retries it.
    this.name = 'AbortError'
    this.heldMs = heldMs
  }
}

const DEFAULT_MAX_HOLD_MS = 120_000
const DEFAULT_LEASE_MS = 10_000
const POLL_MS = 250

interface Lease {
  owner: string
  expiresAt: number
}

function parseLease(raw: string | null): Lease | null {
  if (!raw) return null
  try {
    const v: unknown = JSON.parse(raw)
    if (v && typeof v === 'object') {
      const { owner, expiresAt } = v as { owner?: unknown; expiresAt?: unknown }
      if (typeof owner === 'string' && typeof expiresAt === 'number') return { owner, expiresAt }
    }
  } catch {
    /* a corrupt lease is no lease */
  }
  return null
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function defaultLocks(): LockManagerLike | null {
  const nav = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator
  return nav?.locks && typeof nav.locks.request === 'function' ? nav.locks : null
}

function defaultLease(): LeaseStore | null {
  try {
    const ls = (globalThis as { localStorage?: LeaseStore }).localStorage
    return ls ?? null
  } catch {
    return null // a sandboxed frame throws on access
  }
}

function defaultChannel(name: string): BroadcastChannelLike | null {
  const BC = (globalThis as { BroadcastChannel?: new (name: string) => BroadcastChannelLike }).BroadcastChannel
  if (typeof BC !== 'function') return null
  try {
    return new BC(name)
  } catch {
    return null
  }
}

function makeTabId(random: () => number): string {
  return `tab-${Math.floor(random() * 0xffffffff).toString(36)}-${Math.floor(random() * 0xffffffff).toString(36)}`
}

/** A FIFO mutex inside one tab. `tryAcquire` is skip mode. */
function createLocalMutex(): { acquire(): Promise<() => void>; tryAcquire(): (() => void) | null } {
  let held = false
  const waiting: Array<() => void> = []
  const release = (): void => {
    const next = waiting.shift()
    if (next) next()
    else held = false
  }
  return {
    acquire() {
      if (!held) {
        held = true
        return Promise.resolve(release)
      }
      return new Promise((resolve) => waiting.push(() => resolve(release)))
    },
    tryAcquire() {
      if (held) return null
      held = true
      return release
    },
  }
}

export function createSyncLock(cfg: SyncLockConfig): SyncLock {
  const name = cfg.name
  const maxHoldMs = cfg.maxHoldMs ?? DEFAULT_MAX_HOLD_MS
  const leaseMs = cfg.leaseMs ?? DEFAULT_LEASE_MS
  const now = cfg.now ?? (() => Date.now())
  const random = cfg.random ?? Math.random
  const tabId = cfg.tabId ?? makeTabId(random)
  const locks = cfg.locks === undefined ? defaultLocks() : cfg.locks
  const lease = locks ? null : cfg.lease === undefined ? defaultLease() : cfg.lease
  const channel = locks || !lease ? null : cfg.channel === undefined ? defaultChannel(`${name}:lock`) : cfg.channel
  const leaseKey = `${name}:lease`
  const local = createLocalMutex()
  const layer: SyncLock['layer'] = locks ? 'web-locks' : lease ? 'lease' : 'in-tab'

  let holds = 0
  /** > 0 only while a held fn's SYNCHRONOUS part is on the stack (see `hold`). */
  let inFnSync = 0
  const releaseCallbacks: Array<() => void> = []

  function drainReleaseCallbacks(): void {
    while (holds === 0 && releaseCallbacks.length > 0) {
      const cb = releaseCallbacks.shift()
      try {
        cb?.()
      } catch {
        /* a callback's failure is its own */
      }
    }
  }

  /** Run fn as the holder: the abort at maxHoldMs, and release on settle OR on the timeout. */
  async function hold<T>(fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
    const controller = new AbortController()
    const started = now()
    holds += 1
    let timer: ReturnType<typeof setTimeout> | undefined
    const timedOut = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        const error = new SyncLockTimeoutError(name, now() - started)
        controller.abort(error)
        reject(error)
      }, maxHoldMs)
    })
    try {
      // The synchronous part of fn runs with `inFnSync` raised: a `run(…, 'wait')` reached from
      // there is re-entry for certain, and `run` throws. After fn's first await nothing can tell a
      // re-entrant call from a concurrent one (a browser has no async context), so it queues.
      let pending: Promise<T>
      inFnSync += 1
      try {
        pending = fn(controller.signal)
      } finally {
        inFnSync -= 1
      }
      return await Promise.race([pending, timedOut])
    } finally {
      clearTimeout(timer)
      holds -= 1
    }
  }

  // ------------------------------------------------------------------ lease (fallback 2)
  function readLease(): Lease | null {
    try {
      return parseLease(lease?.getItem(leaseKey) ?? null)
    } catch {
      return null
    }
  }

  function writeLease(): void {
    lease?.setItem(leaseKey, JSON.stringify({ owner: tabId, expiresAt: now() + leaseMs } satisfies Lease))
  }

  function freeLease(): void {
    try {
      if (readLease()?.owner === tabId) lease?.removeItem(leaseKey)
    } catch {
      /* storage gone: the lease expires on its own */
    }
    try {
      channel?.postMessage({ type: 'release', name })
    } catch {
      /* a closed channel: waiters poll */
    }
  }

  /** Wait until a release message arrives or POLL_MS passes, whichever is first. */
  function waitForRelease(): Promise<void> {
    return new Promise((resolve) => {
      let done = false
      const finish = (): void => {
        if (done) return
        done = true
        clearTimeout(t)
        channel?.removeEventListener('message', onMessage)
        resolve()
      }
      const onMessage = (event: MessageEvent): void => {
        const data: unknown = event.data
        if (data && typeof data === 'object' && (data as { type?: unknown }).type === 'release') finish()
      }
      const t = setTimeout(finish, POLL_MS)
      channel?.addEventListener('message', onMessage)
    })
  }

  /** Take the lease. `true` when this tab now owns it; `false` only in skip mode. */
  async function takeLease(mode: LockMode): Promise<boolean> {
    for (;;) {
      const current = readLease()
      if (current && current.owner !== tabId && current.expiresAt > now()) {
        if (mode === 'skip') return false
        await waitForRelease()
        continue
      }
      // Free or expired (a crashed tab): write, let any racing tab write too, then read back.
      writeLease()
      await delay(10 + Math.floor(random() * 31))
      if (readLease()?.owner === tabId) return true
      if (mode === 'skip') return false
    }
  }

  async function runWithLease<T>(fn: (signal: AbortSignal) => Promise<T>, mode: LockMode): Promise<LockRunResult<T>> {
    if (!(await takeLease(mode))) return { ran: false }
    const renew = setInterval(() => {
      if (readLease()?.owner === tabId) writeLease()
    }, Math.max(1, Math.floor(leaseMs / 3)))
    const onPageHide = (): void => freeLease()
    const g = globalThis as { addEventListener?: (t: string, l: () => void) => void; removeEventListener?: (t: string, l: () => void) => void }
    g.addEventListener?.('pagehide', onPageHide)
    try {
      return { ran: true, value: await hold(fn) }
    } finally {
      clearInterval(renew)
      g.removeEventListener?.('pagehide', onPageHide)
      freeLease()
    }
  }

  // ------------------------------------------------------------------ run
  async function run<T>(fn: (signal: AbortSignal) => Promise<T>, mode: LockMode): Promise<LockRunResult<T>> {
    // Re-entry (a 'wait' from inside the held fn) would wait on itself. A CONCURRENT caller in this
    // tab, one that did not start inside the held fn, is not re-entry: it queues behind the holder
    // like a caller in another tab (Web Locks and the in-tab chain are both FIFO). Until 2026-10-08
    // any 'wait' while this tab held the lock threw, and trakr, quil and contently each built an
    // in-tab queue around that.
    if (mode === 'wait' && inFnSync > 0) {
      throw new Error(
        `syncLock "${name}": run(…, 'wait') was called from inside the held fn, which would wait on itself. ` +
          'Open prompts and resolutions with afterRelease(), outside the held drain.',
      )
    }
    try {
      if (locks) {
        return await locks.request(name, mode === 'skip' ? { ifAvailable: true } : {}, async (granted) => {
          if (granted === null) return { ran: false } as LockRunResult<T>
          return { ran: true, value: await hold(fn) } as LockRunResult<T>
        })
      }
      // The in-tab chain is always underneath the lease: two runs in one tab never share it.
      const release = mode === 'skip' ? local.tryAcquire() : await local.acquire()
      if (!release) return { ran: false }
      try {
        if (lease) return await runWithLease(fn, mode)
        return { ran: true, value: await hold(fn) }
      } finally {
        release()
      }
    } finally {
      drainReleaseCallbacks()
    }
  }

  return {
    run,
    heldHere: () => holds > 0,
    afterRelease(cb) {
      releaseCallbacks.push(cb)
      drainReleaseCallbacks()
    },
    layer,
  }
}

// ---------------------------------------------------------------------------------- tab bus

export type TabMessage = { type: 'conflicts-changed' | 'data-changed'; key?: string }

export interface TabBus {
  post(message: TabMessage): void
  /** Messages from OTHER tabs. Returns the unsubscribe. */
  subscribe(listener: (message: TabMessage) => void): () => void
  close(): void
}

export interface TabBusConfig {
  /** `undefined` = a `BroadcastChannel(name)` when present; `null` = use the storage fallback. */
  channel?: BroadcastChannelLike | null
  /** The `storage`-event fallback: where to write, and how to hear other tabs' writes. */
  storage?: {
    store: LeaseStore
    /** Subscribe to `storage` events; give back `(key, newValue)`. Returns the unsubscribe. */
    listen(onChange: (key: string | null, newValue: string | null) => void): () => void
  } | null
  random?: () => number
}

function asTabMessage(data: unknown): TabMessage | null {
  if (!data || typeof data !== 'object') return null
  const { type, key } = data as { type?: unknown; key?: unknown }
  if (type !== 'conflicts-changed' && type !== 'data-changed') return null
  return typeof key === 'string' ? { type, key } : { type }
}

function defaultStorageBus(): TabBusConfig['storage'] {
  const store = defaultLease()
  const g = globalThis as { addEventListener?: (t: string, l: (e: StorageEvent) => void) => void; removeEventListener?: (t: string, l: (e: StorageEvent) => void) => void }
  if (!store || typeof g.addEventListener !== 'function') return null
  return {
    store,
    listen(onChange) {
      const l = (e: StorageEvent): void => onChange(e.key, e.newValue)
      g.addEventListener?.('storage', l)
      return () => g.removeEventListener?.('storage', l)
    },
  }
}

/**
 * How a resolution in one tab closes the chooser in the others: `{ type: 'conflicts-changed' }`.
 * A BroadcastChannel, else a `storage` event (a write to `${name}:bus`), else nothing (one tab).
 */
export function createTabBus(name: string, cfg: TabBusConfig = {}): TabBus {
  const channel = cfg.channel === undefined ? defaultChannel(name) : cfg.channel
  const storage = channel ? null : cfg.storage === undefined ? defaultStorageBus() : cfg.storage
  const random = cfg.random ?? Math.random
  const key = `${name}:bus`
  const listeners = new Set<(m: TabMessage) => void>()
  const unsubs: Array<() => void> = []
  const deliver = (m: TabMessage | null): void => {
    if (m) for (const l of [...listeners]) l(m)
  }

  if (channel) {
    const onMessage = (event: MessageEvent): void => deliver(asTabMessage(event.data))
    channel.addEventListener('message', onMessage)
    unsubs.push(() => channel.removeEventListener('message', onMessage))
  } else if (storage) {
    unsubs.push(
      storage.listen((k, v) => {
        if (k !== key || !v) return
        try {
          deliver(asTabMessage((JSON.parse(v) as { m?: unknown }).m))
        } catch {
          /* not ours */
        }
      }),
    )
  }

  return {
    post(message) {
      try {
        if (channel) channel.postMessage(message)
        // The nonce makes every post a change, so a repeat still fires a storage event.
        else storage?.store.setItem(key, JSON.stringify({ m: message, n: random() }))
      } catch {
        /* other tabs catch up on their next reconcile */
      }
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    close() {
      for (const u of unsubs.splice(0)) u()
      listeners.clear()
    },
  }
}
